import React, {useState, useEffect, useRef} from 'react';
import {useHistory} from "react-router-dom";
import {withTranslation} from 'react-i18next';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from "primereact/button";
import {Panel} from "primereact/panel";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Calendar} from "primereact/calendar";
import {TriStateCheckbox} from "primereact/tristatecheckbox";
import axios from "axios";
import moment from "moment/moment";
import {Badge} from "primereact/badge";

const InvoiceApprovalListComponent = (props) => {

  const {t, onDetailedSearchActiveChange} = props;
  const history = useHistory();
  const DEFAULT_SORT_FIELD = 'id';
  const DEFAULT_SORT_ORDER = -1;
  const DEFAULT_FILTERS = {
    in_review: { value: true, matchMode: 'equals' }
  };
  const defaultRows = Number(process.env.REACT_APP_LIST_ROW_DEFAULT);
  const saved = (() => {
    try {
      const raw = localStorage.getItem('lastInvoiceApprovalFilter');
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  })();
  const savedDetailed = (() => {
    try {
      const raw = localStorage.getItem('lastInvoiceApprovalDetailedSearchForm');
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  })();
  const savedDetailedQuery = (savedDetailed?.detailedSearchQuery || '').trim();
  const rerenderInvoiceApprovalList = window.App?.state?.rerenderInvoiceApprovalList;
  const [invoiceApprovals, setInvoiceApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rows, setRows] = useState(saved?.rows ?? defaultRows);
  const dt = useRef(null);
  const detailedSearchVisible = props.detailedSearchVisible !== undefined ? props.detailedSearchVisible : !!savedDetailedQuery;
  const [detailedSearchQuery, setDetailedSearchQuery] = useState(savedDetailed?.detailedSearchQuery || '');
  const isDetailedSearchActive = React.useMemo(() => {
    return (detailedSearchQuery || '').toString().trim().length > 0;
  }, [detailedSearchQuery]);
  const [dsIssueDateFrom, setDsIssueDateFrom] = useState(savedDetailed?.dsIssueDateFrom || null);
  const [dsIssueDateTo, setDsIssueDateTo] = useState(savedDetailed?.dsIssueDateTo || null);
  const [dsPaymentDateFrom, setDsPaymentDateFrom] = useState(savedDetailed?.dsPaymentDateFrom || null);
  const [dsPaymentDateTo, setDsPaymentDateTo] = useState(savedDetailed?.dsPaymentDateTo || null);
  const [dsDeliveredDateFrom, setDsDeliveredDateFrom] = useState(savedDetailed?.dsDeliveredDateFrom || null);
  const [dsDeliveredDateTo, setDsDeliveredDateTo] = useState(savedDetailed?.dsDeliveredDateTo || null);
  const [dsReceiptNumber, setDsReceiptNumber] = useState(savedDetailed?.dsReceiptNumber || '');
  const [dsPartner, setDsPartner] = useState(savedDetailed?.dsPartner || '');
  const [dsIsPaid, setDsIsPaid] = useState(savedDetailed?.dsIsPaid ?? null);
  const [dsCostPosted, setDsCostPosted] = useState(savedDetailed?.dsCostPosted ?? null);
  const [dsNetAmountMin, setDsNetAmountMin] = useState(savedDetailed?.dsNetAmountMin ?? null);
  const [dsNetAmountMax, setDsNetAmountMax] = useState(savedDetailed?.dsNetAmountMax ?? null);
  const [dsGrossAmountMin, setDsGrossAmountMin] = useState(savedDetailed?.dsGrossAmountMin ?? null);
  const [dsGrossAmountMax, setDsGrossAmountMax] = useState(savedDetailed?.dsGrossAmountMax ?? null);
  const [lazyParams, setLazyParams] = useState(() => {
    if (saved) {
      return {
        first: saved.first ?? 0,
        rows: saved.rows ?? defaultRows,
        page: saved.page ?? 0,
        sortField: saved.sortField || DEFAULT_SORT_FIELD,
        sortOrder: typeof saved.sortOrder === 'number' ? saved.sortOrder : DEFAULT_SORT_ORDER,
        filters: {
          ...DEFAULT_FILTERS,
          ...(saved.filters || {})
        }
      };
    }
    return {
      first: 0,
      rows: defaultRows,
      page: 0,
      sortField: DEFAULT_SORT_FIELD,
      sortOrder: DEFAULT_SORT_ORDER,
      filters: DEFAULT_FILTERS,
    };
  });

  const persistListState = React.useCallback((state) => {
    localStorage.setItem('lastInvoiceApprovalFilter', JSON.stringify({
      ...state,
      sortField: state?.sortField || DEFAULT_SORT_FIELD,
      sortOrder: typeof state?.sortOrder === 'number' ? state.sortOrder : DEFAULT_SORT_ORDER,
    }));
  }, [DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER]);

  const persistDetailedForm = (query = detailedSearchQuery) => {
    const payload = {
      dsIssueDateFrom,
      dsIssueDateTo,
      dsPaymentDateFrom,
      dsPaymentDateTo,
      dsDeliveredDateFrom,
      dsDeliveredDateTo,
      dsReceiptNumber,
      dsPartner,
      dsIsPaid,
      dsCostPosted,
      dsNetAmountMin,
      dsNetAmountMax,
      dsGrossAmountMin,
      dsGrossAmountMax,
      detailedSearchQuery: query,
    };
    localStorage.setItem('lastInvoiceApprovalDetailedSearchForm', JSON.stringify(payload));
  };

  const clearDetailedFormPersist = () => {
    localStorage.removeItem('lastInvoiceApprovalDetailedSearchForm');
  };

  const parseCalendarRangeValue = (from, to) => {
    const values = [];
    const fromMoment = from ? moment(from, 'YYYY-MM-DD', true) : null;
    const toMoment = to ? moment(to, 'YYYY-MM-DD', true) : null;
    if (fromMoment && fromMoment.isValid()) {
      values.push(fromMoment.toDate());
    }
    if (toMoment && toMoment.isValid()) {
      values.push(toMoment.toDate());
    }
    return values.length ? values : null;
  };

  const handleCalendarRangeChange = (range, setFrom, setTo) => {
    const [from, to] = range || [];
    if (!from) {
      setFrom(null);
      setTo(null);
      return;
    }
    setFrom(moment(from).format('YYYY-MM-DD'));
    setTo(to ? moment(to).format('YYYY-MM-DD') : null);
  };

  const buildDateSearchParams = (field, from, to) => {
    if (from && to) {
      return `search[${field}][operator]=between&search[${field}][value]=${from} 00:00:00,${to} 23:59:59`;
    }
    if (from && !to) {
      return `search[${field}][operator]=>=&search[${field}][value]=${from} 00:00:00`;
    }
    if (!from && to) {
      return `search[${field}][operator]=<=&search[${field}][value]=${to} 23:59:59`;
    }
    return '';
  };

  const buildAmountSearchParams = (field, min, max) => {
    if (min !== null && max !== null) {
      return `search[${field}][operator]=between&search[${field}][value]=${min},${max}`;
    }
    if (min !== null) {
      return `search[${field}][operator]=>=&search[${field}][value]=${min}`;
    }
    if (max !== null) {
      return `search[${field}][operator]=<=&search[${field}][value]=${max}`;
    }
    return '';
  };

  const buildDetailedSearchQuery = () => {
    let parts = [];
    if (dsReceiptNumber && dsReceiptNumber.toString().trim() !== '') {
      const val = dsReceiptNumber.toString().trim();
      parts.push(`search[receipt_number][operator]=like&search[receipt_number][value]=*${encodeURIComponent(val)}*`);
    }
    if (dsPartner && dsPartner.toString().trim() !== '') {
      parts.push(`search[partner_name][operator]=like&search[partner_name][value]=*${encodeURIComponent(dsPartner.toString().trim())}*`);
    }
    if (dsIsPaid !== null) {
      parts.push(`search[is_paid][operator]==&search[is_paid][value]=${dsIsPaid}`);
    }
    if (dsCostPosted !== null) {
      parts.push(`search[cost_posted][operator]==&search[cost_posted][value]=${dsCostPosted}`);
    }
    const net = buildAmountSearchParams('net_amount', dsNetAmountMin, dsNetAmountMax);
    if (net) parts.push(net);
    const gross = buildAmountSearchParams('gross_amount', dsGrossAmountMin, dsGrossAmountMax);
    if (gross) parts.push(gross);
    const issue = buildDateSearchParams('issue_date', dsIssueDateFrom, dsIssueDateTo);
    if (issue) parts.push(issue);
    const payment = buildDateSearchParams('payment_date', dsPaymentDateFrom, dsPaymentDateTo);
    if (payment) parts.push(payment);
    const delivered = buildDateSearchParams('delivery_date', dsDeliveredDateFrom, dsDeliveredDateTo);
    if (delivered) parts.push(delivered);
    return parts.filter(Boolean).join('&');
  };

  const fetchInvoiceApprovalList = (queryOrParams) => {
    let query = queryOrParams;
    let extraDetailed = '';
    if (queryOrParams && typeof queryOrParams === 'object' && queryOrParams !== null) {
      extraDetailed = queryOrParams.detailedSearchQuery || '';
      try {
        let paramsForTransform = {...queryOrParams};
        delete paramsForTransform.detailedSearchQuery;
        if (window.App && typeof window.App.transformQueryParams === 'function') {
          query = window.App.transformQueryParams(paramsForTransform);
        } else {
          query = JSON.stringify(paramsForTransform);
        }
      } catch (error) {
        console.error('InvoiceApprovalList query transform error:', error);
        query = queryOrParams;
      }
    }
    if (query && query.toString().includes(',"multiSortMeta":[]')) {
      query = query.replace(',"multiSortMeta":[]', '');
    }
    if (extraDetailed) {
      if (!query || query.toString().length === 0) {
        query = '?' + extraDetailed;
      } else if (query.toString().includes('?')) {
        query = query + (query.toString().endsWith('?') || query.toString().endsWith('&') ? '' : '&') + extraDetailed;
      } else {
        query = '?' + query + '&' + extraDetailed;
      }
    }
    return axios({
      method: 'get',
      url: process.env.REACT_APP_API_HOST + '/api/v2/receipt' + query,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem('userToken'),
        'App-Network': localStorage.getItem('networkId')
      }
    });
  };

  const loadLazyTimeout = useRef(null);
  const loadLazyData = React.useCallback(() => {
    setLoading(true);
    if (loadLazyTimeout.current) {
      clearTimeout(loadLazyTimeout.current);
    }
    loadLazyTimeout.current = setTimeout(() => {
      let _lazyParams = {...lazyParams};
      _lazyParams.filters = {
        ...(_lazyParams.filters || {}),
        in_review: { value: true, matchMode: 'equals' },
      };
      _lazyParams.detailedSearchQuery = detailedSearchQuery || '';
      persistListState(_lazyParams);
      fetchInvoiceApprovalList(_lazyParams)
        .then(response => {
          setTotalRecords(response.data.data.total_item_count);
          setReceipt(response.data.data.items);
          setInvoiceApprovals(response.data.data.items);
        })
        .catch(error => {
          if (window.App?.toastShow) {
            window.App.toastShow('error', t('error'), error.toString(), '', error.response);
          } else {
            console.error(error);
          }
        })
        .finally(() => {
          setLoading(false);
        })
    }, Math.random() * 1000 + 250);
  }, [detailedSearchQuery, lazyParams, persistListState, t]);
  const onPage = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onSort = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onFilter = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    _lazyParams['first'] = 0;
    _lazyParams['page'] = 0;
    if (!_lazyParams.filters) _lazyParams.filters = {};
    _lazyParams.filters.in_review = { value: true, matchMode: 'equals' };
    setLazyParams(_lazyParams);
  }
  const pager = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink',
    'RowsPerPageDropdown': (options) => {
      const dropdownOptions = [
        {label: Number(process.env.REACT_APP_LIST_ROW_OP1), value: Number(process.env.REACT_APP_LIST_ROW_OP1)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP2), value: Number(process.env.REACT_APP_LIST_ROW_OP2)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP3), value: Number(process.env.REACT_APP_LIST_ROW_OP3)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP4), value: Number(process.env.REACT_APP_LIST_ROW_OP4)},
      ];

      return (
        <>
          {/*<span className="p-mx-1" style={{color: 'var(--text-color)', userSelect: 'none'}}>Tételek egy oldalon: </span>*/}
          <Dropdown value={options.value} options={dropdownOptions} onChange={(e) => setRows(e.target.value)}
                    appendTo={document.body}/>
        </>
      );
    },
    'CurrentPageReport': (options) => {
      return (
        <span style={{color: 'var(--text-color)', userSelect: 'none', width: '120px', textAlign: 'center'}}>
                        {options.first} - {options.last} / {options.totalRecords}
        </span>
      )
    }
  };

  const renderCurrencyTotals = (totalsMap) => {
    if (!totalsMap || totalsMap.size === 0) return null;
    const priority = ['HUF', 'EUR'];
    const entries = Array.from(totalsMap.entries()).sort(([a], [b]) => {
      const ai = priority.indexOf(a);
      const bi = priority.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return String(a).localeCompare(String(b));
    });
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
        {entries.map(([currency, sum]) => (
          <div key={currency} style={{ whiteSpace: 'nowrap' }}>
            {(window?.App?.currencyFormatter
              ? window.App.currencyFormatter(currency, sum)
              : `${sum} ${currency}`)}
          </div>
        ))}
      </div>
    );
  };

  const totalsByNetAmount = React.useMemo(() => {
    const map = new Map();
    (receipt || []).forEach(row => {
      const currency = row?.currency || 'HUF';
      const amount = Number(row?.net_amount);
      if (Number.isFinite(amount)) {
        map.set(currency, (map.get(currency) || 0) + amount);
      }
    });
    return map;
  }, [receipt]);

  const totalsByGrossAmount = React.useMemo(() => {
    const map = new Map();
    (receipt || []).forEach(row => {
      const currency = row?.currency || 'HUF';
      const amount = Number(row?.gross_amount);
      if (Number.isFinite(amount)) {
        map.set(currency, (map.get(currency) || 0) + amount);
      }
    });
    return map;
  }, [receipt]);

  
  const netFooter = React.useMemo(() => renderCurrencyTotals(totalsByNetAmount), [totalsByNetAmount]);
  const grossFooter = React.useMemo(() => renderCurrencyTotals(totalsByGrossAmount), [totalsByGrossAmount]);

  const formatCurrency = (currency, amount) => {
    if (window.App && typeof window.App.currencyFormatter === 'function') {
      return window.App.currencyFormatter(currency, amount)
    }
    return <>{amount}&nbsp;{currency}</>
  }

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <Button icon="pi pi-eye" className=" p-button p-mr-2"
                tooltip={t('view')} tooltipOptions={{ position: 'left' }}
                onClick={() => {
                  history.push('/finance/invoice-approval/read/' + rowData.id,
                    {
                      from: history.location.pathname + history.location.search
                    }
                  )
                }}/>
      </React.Fragment>
    );
  }

  useEffect(() => {
    loadLazyData();
  }, [loadLazyData, rerenderInvoiceApprovalList])  

  useEffect(() => {
    if (typeof onDetailedSearchActiveChange === 'function') {
      onDetailedSearchActiveChange(isDetailedSearchActive);
    }
  }, [isDetailedSearchActive, onDetailedSearchActiveChange]);

  return (
    <>
      {detailedSearchVisible &&
        <Panel className="p-mb-2" onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (document.activeElement) document.activeElement.blur();
              setTimeout(() => {
                const searchBtn = document.querySelector('button.p-button-primary .pi-search')?.parentElement;
                if (searchBtn) searchBtn.click();
              }, 100);
            }
          }}>
          <div className={"p-grid"}>
            <div className={"p-col-12 p-lg-2"}>
              <div className="p-field p-fluid">
                <label>{t('columnPartner')}</label>
                <InputText
                  value={dsPartner}
                  onChange={(e) => setDsPartner(e.target.value)}
                />
              </div>
            </div>

            <div className={"p-col-12 p-lg-2"}>
              <div className="p-field p-fluid">
                <label>{t('columnSerialNumber')}</label>
                <InputText
                  value={dsReceiptNumber}
                  onChange={(e) => setDsReceiptNumber(e.target.value)}
                  placeholder={t('dsExampleSerialNumber')}
                />
              </div>
            </div>

            <div className={"p-col-12 p-lg-2"}>
              <div className="p-field p-fluid">
                <label>{t('fieldIssueDate')}</label>
                <Calendar
                  value={parseCalendarRangeValue(dsIssueDateFrom, dsIssueDateTo)}
                  onChange={(e) =>
                    handleCalendarRangeChange(
                      e.value,
                      setDsIssueDateFrom,
                      setDsIssueDateTo,
                    )
                  }
                  selectionMode="range"
                  readOnlyInput
                  showIcon
                  dateFormat="yy-mm-dd"
                  appendTo={document.body}
                  placeholder={t('dsFromTo')}
                  className="w-100"
                />
              </div>
            </div>

            <div className={"p-col-12 p-lg-2"}>
              <div className="p-field p-fluid">
                <label>{t('columnPaymentDeadline')}</label>
                <Calendar
                  value={parseCalendarRangeValue(dsPaymentDateFrom, dsPaymentDateTo)}
                  onChange={(e) =>
                    handleCalendarRangeChange(
                      e.value,
                      setDsPaymentDateFrom,
                      setDsPaymentDateTo,
                    )
                  }
                  selectionMode="range"
                  readOnlyInput
                  showIcon
                  dateFormat="yy-mm-dd"
                  appendTo={document.body}
                  placeholder={t('dsFromTo')}
                  className="w-100"
                />
              </div>
            </div>

            <div className={"p-col-12 p-lg-2"}>
              <div className="p-field p-fluid">
                <label>{t('columnFulfillment')}</label>
                <Calendar
                  value={parseCalendarRangeValue(dsDeliveredDateFrom, dsDeliveredDateTo)}
                  onChange={(e) =>
                    handleCalendarRangeChange(
                      e.value,
                      setDsDeliveredDateFrom,
                      setDsDeliveredDateTo,
                    )
                  }
                  selectionMode="range"
                  readOnlyInput
                  showIcon
                  dateFormat="yy-mm-dd"
                  appendTo={document.body}
                  placeholder={t('dsFromTo')}
                  className="w-100"
                />
              </div>
            </div>
            <div className={"p-col-12 p-lg-2"} ></div>
             <div className={"p-col-12 p-lg-2"}>
              <div className="p-field p-fluid">
                <label>{t('columnPaid2')}</label>
                <div className="p-fluid p-py-1">
                  <TriStateCheckbox
                    value={dsIsPaid}
                    onChange={(e) => setDsIsPaid(e.value)}
                  />
                </div>
              </div>
            </div>

            <div className={"p-col-12 p-lg-2"}>
              <div className="p-field p-fluid">
                <label>{t('columnCostsRecorded')}</label>
                <div className="p-fluid p-py-1">
                  <TriStateCheckbox
                    value={dsCostPosted}
                    onChange={(e) => setDsCostPosted(e.value)}
                  />
                </div>
              </div>
            </div>

            <div className={"p-col-12 p-lg-8"} />

            <div className={"p-col-12 p-lg-2"}>
              <div className="p-field p-fluid">
                <label>{t('columnNetAmount')}</label>
                <div className={"p-grid"}>
                  <div className={"p-col-5"}>
                    <InputNumber
                      value={dsNetAmountMin}
                      onValueChange={(e) => setDsNetAmountMin(e.value)}
                      placeholder={t('dsMin')}
                    />
                  </div>
                  <div className={"p-col-2 p-text-center p-col-align-center p-px-0"}>
                    <i className={"pi pi-arrow-left"}></i> <i className={"pi pi-arrow-right"}></i>
                  </div>
                  <div className={"p-col-5"}>
                    <InputNumber
                      value={dsNetAmountMax}
                      onValueChange={(e) => setDsNetAmountMax(e.value)}
                      placeholder={t('dsMax')}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={"p-col-12 p-lg-2"}>
              <div className="p-field p-fluid">
                <label>{t('columnGrossAmount')}</label>
                <div className={"p-grid"}>
                  <div className={"p-col-5"}>
                    <InputNumber
                      value={dsGrossAmountMin}
                      onValueChange={(e) => setDsGrossAmountMin(e.value)}
                      placeholder={t('dsMin')}
                    />
                  </div>
                  <div className={"p-col-2 p-text-center p-col-align-center p-px-0"}>
                    <i className={"pi pi-arrow-left"}></i> <i className={"pi pi-arrow-right"}></i>
                  </div>
                  <div className={"p-col-5"}>
                    <InputNumber
                      value={dsGrossAmountMax}
                      onValueChange={(e) => setDsGrossAmountMax(e.value)}
                      placeholder={t('dsMax')}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={"p-col-12 p-lg-8"} />

            <div className={"p-col-12 p-text-right"}>
              <Button
                type="button"
                label={t('dsClearCriteria')}
                icon="pi pi-times"
                className="p-button-danger p-mr-2"
                onClick={() => {
                  setDsIssueDateFrom(null);
                  setDsIssueDateTo(null);
                  setDsPaymentDateFrom(null);
                  setDsPaymentDateTo(null);
                  setDsDeliveredDateFrom(null);
                  setDsDeliveredDateTo(null);
                  setDetailedSearchQuery('');
                  setDsReceiptNumber('');
                  setDsPartner('');
                  setDsIsPaid(null);
                  setDsCostPosted(null);
                  setDsNetAmountMax(null);
                  setDsNetAmountMin(null);
                  setDsGrossAmountMax(null);
                  setDsGrossAmountMin(null);
                  clearDetailedFormPersist();
                  setLazyParams({
                    first: 0,
                    rows: rows,
                    page: 0,
                    sortField: DEFAULT_SORT_FIELD,
                    sortOrder: DEFAULT_SORT_ORDER,
                    filters: {
                      in_review: { value: true, matchMode: 'equals' }
                    }
                  });
                }}
              />

              <Button
                type="button"
                label={t('dsSearch')}
                icon="pi pi-search"
                className="p-button-primary"
                onClick={() => {
                  const q = buildDetailedSearchQuery();
                  setDetailedSearchQuery(q);
                  persistDetailedForm(q);
                  setLazyParams({
                    ...lazyParams,
                    first: 0,
                    page: 0,
                  });
                }}
              />
            </div>
          </div>
        </Panel>
      }
      <DataTable size={localStorage.getItem('scale')}
                 emptyMessage={t('noResults')}
                 value={invoiceApprovals} paginator first={lazyParams.first} totalRecords={totalRecords} onPage={onPage}
                 onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                 onFilter={onFilter} filters={lazyParams.filters} loading={loading} lazy
                 paginatorTemplate={pager} rows={rows}
                 autoLayout={true}
                 filterDisplay="row"
                 responsiveLayout="scroll"
                 stripedRows
                 ref={dt}
                 dataKey="id"
      >
        {/* <Column field="receipt_type" header={t('columnType')} body={receiptTypeBodyTemplate}></Column> */}
        <Column field="receipt_number" header={t('columnSerialNumber')} sortable body={(rowData) => {
          const severity = rowData?.draft ? 'warning' : 'success';
          return (
            <Badge
              value={rowData?.receipt_number}
              severity={severity}
            />
          );
        }}></Column>
        <Column header={t('columnPartner')} bodyClassName="col-body-font-bold" sortable body={(rowData) => rowData.partner?.name || rowData.partner_name || ''}></Column>
        <Column field="issue_date" header={t('columnIssueDate')} sortable className={"p-text-nowrap"} body={(rowData) => {
          return (
            <>
            {rowData.issue_date ? window.App.formatDateValue(rowData.issue_date) : ''}
            </>
          )
        }}></Column>
        <Column field="delivery_date" header={t('columnFulfillment')} sortable className={"p-text-nowrap"} body={(rowData) => {
          return (
            <>{window.App.formatDateValue(rowData.delivery_date)}</>
          )
        }}></Column>
        <Column field="cost_posted" header={t('columnCostsRecorded')} sortable className={"p-text-center p-text-nowrap"}
                body={(rowData) => {
                  if (rowData.cost_posted === true) {
                    return (
                      <>
                        <i className={"pi pi-check text-success"}></i>
                      </>
                    )
                  } else {
                    return (
                      <>
                        <i className={"pi pi-times text-danger"}></i>
                      </>
                    )
                  }
        }}></Column>
        <Column field="is_paid" header={t('columnPaid2')} sortable className={"p-text-center p-text-nowrap"}
          body={(rowData) => {
            if (rowData.is_paid === true) {
              return (
                <>
                  <i className={"pi pi-check text-success"}></i>
                  {rowData.payment_date !== null && props.view === 'advanced' &&
                    <>
                      <br/>
                      {window.App.formatDateValue(rowData.payment_date)}
                    </>
                  }
                </>
              )
            } else {
              return (
                <>
                  <i className={"pi pi-times text-danger"}></i>
                </>
              )
            }
          }}></Column>
        <Column
          field="net_amount"
          header={t('columnNetAmount')}
          sortable
          className={"p-text-right p-text-nowrap"}
          alignHeader="right"
          footer={netFooter}
          footerStyle={{ textAlign: 'right', fontWeight: 600 }}
          body={(rowData) => formatCurrency(rowData.currency, rowData.net_amount || 0)}
        />
        {props.receiptType !== 'delivery_note' && props.view === 'advanced' &&
          <Column field="vat_amount" header={t('columnVatAmount')} sortable className={"p-text-right p-text-nowrap"}
                  alignHeader="right"
                  body={(rowData) => formatCurrency(rowData.currency, rowData.vat_amount || 0)}></Column>
        }
        {props.receiptType !== 'delivery_note' &&
          <Column
            field="gross_amount"
            header={t('columnGrossAmount')}
            sortable
            className={"p-text-right p-text-nowrap"}
            alignHeader="right"
            footer={grossFooter}
            footerStyle={{ textAlign: 'right', fontWeight: 600 }}
            body={(rowData) => formatCurrency(rowData.currency, rowData.gross_amount || 0)}
          />
        }



        <Column body={actionBodyTemplate} className={"p-py-0 p-text-right p-text-nowrap"}></Column>
      </DataTable>
    </>
  )
}
export default withTranslation('common')(InvoiceApprovalListComponent);
